import { useState, useMemo } from 'react'

import { Modal, Text, MultiSelect } from '@mantine/core'

import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { SHARE_APPLICANT_FILTER_PRESET } from 'src/graphql/applicantFilterPresets.graphql'
import { GET_EMPLOYEES_ONLY } from 'src/graphql/employees.graphql'

interface ShareViewModalProps {
  opened: boolean
  onClose: () => void
  preset: {
    applicantFilterPresetId: string
    name: string
  } | null
  refetch?: () => void
}

const ShareViewModal = ({
  opened,
  onClose,
  preset,
  refetch,
}: ShareViewModalProps) => {
  const { currentUser } = useAuth()
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([])

  const isAdmin = currentUser?.roles?.includes('ADMIN')

  // Fetch employees for the multi-select
  // Admins can share a view with anyone in the company
  // Everyone else can only share with recruiters
  const { data: employeesData, loading: employeesLoading } = useQuery(
    GET_EMPLOYEES_ONLY,
    {
      variables: {
        pageInfo: { offset: 0, limit: 100 },
        filters: isAdmin ? {} : { roleTypes: ['RECRUITER'] },
      },
      fetchPolicy: 'cache-and-network',
    }
  )

  // Filter out the current user and map employees to select options
  // Note: The employees query only returns ACTIVE employees
  const employeeOptions = useMemo(() => {
    if (!employeesData?.employees?.items) return []

    return employeesData.employees.items
      .filter((employee) => employee.employeeId !== currentUser?.employeeId)
      .map((employee) => ({
        label: `${employee.firstName} ${employee.lastName}`,
        value: employee.employeeId,
      }))
  }, [employeesData, currentUser])

  const [shareFilterPreset, { loading: shareLoading }] = useMutation(
    SHARE_APPLICANT_FILTER_PRESET,
    {
      onCompleted: (data) => {
        const sharedCount = data?.shareApplicantFilterPreset?.length || 0
        toast(
          `View shared with ${sharedCount} employee${sharedCount > 1 ? 's' : ''}`,
          'success'
        )
        handleClose()
        refetch?.()
      },
      onError: (error) => {
        console.error('Error sharing preset:', error?.message)
        toast('Failed to share view', 'error')
      },
    }
  )

  const handleClose = () => {
    setSelectedEmployeeIds([])
    onClose()
  }

  const handleShare = async () => {
    if (!preset?.applicantFilterPresetId || selectedEmployeeIds.length === 0) {
      return
    }

    await shareFilterPreset({
      variables: {
        applicantFilterPresetId: preset.applicantFilterPresetId,
        employeeIds: selectedEmployeeIds,
      },
    })
  }

  const isSubmitDisabled = selectedEmployeeIds.length === 0 || shareLoading

  return (
    <Modal
      opened={opened}
      withCloseButton={false}
      onClose={handleClose}
      closeOnClickOutside={true}
      closeOnEscape={true}
      yOffset={100}
      size="md"
      overlayProps={{
        backgroundOpacity: 0.2,
        blur: 5,
      }}
      classNames={{
        body: 'flex flex-col gap-4',
      }}
    >
      <Text className="text-lg font-semibold text-doubleNickel-gray-900">
        Share View: {preset?.name}
      </Text>

      {/* Employee Selection */}
      <div>
        <Text className="mb-2 text-sm font-medium text-doubleNickel-gray-500">
          Share with:
        </Text>
        <MultiSelect
          placeholder={
            employeesLoading ? 'Loading employees...' : 'Select employees'
          }
          data={employeeOptions}
          value={selectedEmployeeIds}
          onChange={setSelectedEmployeeIds}
          searchable
          clearable
          disabled={employeesLoading}
          maxDropdownHeight={200}
          classNames={{
            pill: 'rounded-md bg-doubleNickel-gray-100',
            pillsList:
              'flex flex-row flex-wrap gap-2 max-w-full overflow-auto',
          }}
        />
      </div>

      {/* Action Buttons */}
      <div className="mt-2 flex w-full flex-row items-center justify-end gap-2">
        <DNButton variant="outline" className="h-9" onClick={handleClose}>
          Cancel
        </DNButton>
        <DNButton
          variant="primary"
          className="h-9 min-w-20"
          disabled={isSubmitDisabled}
          loading={shareLoading}
          onClick={handleShare}
        >
          Share
        </DNButton>
      </div>
    </Modal>
  )
}

export default ShareViewModal
